import { useEffect, useState } from 'react';
import { PhoneNumberService } from '../services/PhoneNumberService';
import { CountryCode } from 'libphonenumber-js';

const phoneNumberService = new PhoneNumberService();

/**
 * `usePhoneNumberMask` is a custom React hook that provides functionality related to generating
 * a phone number mask based on the provided country code. By default, it initializes with the `BR` country code.
 *
 * The hook keeps track of the current country code and updates the phone number mask whenever the country code changes.
 *
 * @example
 * const { countryCode, setCountryCode, phoneMask } = usePhoneNumberMask();
 * setCountryCode('BR');
 * console.log(phoneMask); // Outputs: '(99) 99999-9999' or whatever format corresponds to the 'BR' country code.
 *
 * @returns
 */
function usePhoneNumberMask(initialCountryCode: CountryCode = 'BR') {
  const [countryCode, setCountryCode] = useState<CountryCode>(initialCountryCode);
  const [phoneMask, setPhoneMask] = useState<string | undefined>(
    phoneNumberService.createPhoneMask(initialCountryCode)
  );
  const [countryCallingCode, setCountryCallingCode] = useState<string | undefined>(
    phoneNumberService.getCountryCallingCode(initialCountryCode)
  );

  useEffect(() => {
    const newMask = phoneNumberService.createPhoneMask(countryCode);
    const newCountryCallingCode = phoneNumberService.getCountryCallingCode(countryCode);

    setPhoneMask(newMask);
    setCountryCallingCode(newCountryCallingCode);
  }, [countryCode]);

  return {
    countryCode,
    countryCallingCode,
    phoneMask,
    setCountryCode
  };
}

export { usePhoneNumberMask };
